import { observable, action, flow, configure } from 'mobx'
import API from '@aws-amplify/api'
import _ from 'lodash'

import search from './search'
import { ToastType, ToastTypeEnum } from '../components/Dashboard/Toasts'
import related from './related'
import { htmlToPlainText } from '../utils/HelperFunctions'

configure({ enforceActions: 'always' })

export interface UserInfo {
  emailLinked: boolean
  isAdmin: boolean
  isLicensed: boolean
  enabledFeatures: string[]
}

export interface EmailTransaction {
  maneuver: string
  isReply: boolean
  missionName: string
  emailBodySnippet: string
  transactionDate: string
  touch: string
}
export interface User {
  username: boolean
}

export interface MultiTemplateDaysBetween {
  [campaign: string]: string[]
}

export interface TemplatesObject {
  followUp: MultiTemplate
  firstTouch: MultiTemplate
  nurture: MultiTemplate
  daysBetween: {
    followUp: {
      [campaign: string]: string
    }
    firstTouch: {
      [campaign: string]: string
    }
    nurture: {
      [campaign: string]: string
    }
  }
}

export interface MasterTemplatesObject {
  followUp: MultiTemplate
  firstTouch: MultiTemplate
  nurture: MultiTemplate
  daysBetween: {
    followUp: MultiTemplateDaysBetween
    firstTouch: MultiTemplateDaysBetween
    nurture: MultiTemplateDaysBetween
  }
}

export interface MultiTemplate {
  [campaign: string]: Template[]
}

export interface Template {
  subject: string
  body: string
  sendOnReply?: boolean
  thread?: boolean
  disabled?: boolean
}

interface PhoneNumber {
  number: string
  category: string
}
export interface RecentTouch {
  touch: string
  sendDate: string
  uuid: string
  openCount: number
}

export interface ActiveMissionItem {
  sendOnReply: boolean
  thread: boolean
  disabled: boolean
}

export interface EmailMetadataObject {
  activeMission: ActiveMissionItem[]
}

export interface Prospect {
  firstName: string
  lastName: string
  industry: string
  templates: TemplatesObject
  followUpTemplates: MultiTemplate
  nurtureTemplates: MultiTemplate
  firstTouchTemplates: MultiTemplate
  engagementSequence: string
  maverickBucket: string
  maverickStatus: string
  seniorityLevel: string
  maneuver: string
  maneuverStatus: string
  sendToMarketing: string
  removeFromMaverick: string
  dnc: string
  operationalBucket: string
  nurtureDaysBetween: MultiTemplateDaysBetween
  followUpDaysBetween: MultiTemplateDaysBetween
  firstTouchDaysBetween: MultiTemplateDaysBetween
  customSendDate: number
  company: string
  copperStatus: string
  phoneNumber?: PhoneNumber
  linkedIn: string
  twitter: string
  instagram: string
  facebook: string
  title: string
  email: string
  id: number
  name: string
  addedBy: string
  notifyOnComplete: boolean
  isHot?: boolean
  rank?: string
  sendDates: RecentTouch[]
  domain: string
  notes?: string
  emailMetadata?: EmailMetadataObject
}

interface ActivateManeuverOption {
  text: string
  checked: boolean
  enabled: boolean
}

const activateManeuverOptions: Record<string, ActivateManeuverOption> = {
  activated: {
    text: 'Maneuver Activated',
    checked: true,
    enabled: true,
  },
  scheduled: {
    text: 'Maneuver Activated',
    checked: true,
    enabled: true,
  },
  ready: {
    text: 'Maneuver Active',
    checked: false,
    enabled: false,
  },
  active: {
    text: 'Maneuver Active',
    checked: true,
    enabled: true,
  },
  inactive: {
    text: 'Maneuver Active',
    checked: false,
    enabled: false,
  },
}

interface TextOptions {
  checked: string
  unchecked: string
}

const sendProspectTextOptions: TextOptions = {
  checked: 'Sent to Marketing',
  unchecked: 'Send to Marketing',
}

const prospectDNCTextOptions: TextOptions = {
  checked: 'Do Not Engage Contact',
  unchecked: 'Do Not Engage Contact',
}

export class Prospects {
  @observable toasts: ToastType[] = []
  @observable isLoading = false
  @observable userInfoIsLoading = true
  @observable userInfo: UserInfo | null = null
  @observable user: User | null = null
  @observable prospect: Prospect | null = null
  @observable sendProspectChecked = false
  @observable sendProspectCheckedText = sendProspectTextOptions.checked
  @observable sendProspectUncheckedText = sendProspectTextOptions.unchecked
  @observable activateManeuverChecked = false
  @observable activateManeuverOption: ActivateManeuverOption = activateManeuverOptions.inactive
  @observable prospectDNCChecked = false
  @observable prospectDNCText = prospectDNCTextOptions.unchecked
  @observable deleteLeadConfirmationOpen = false
  @observable deleteReason: string | null = 'other'
  @observable cacheValid = false
  @observable emailLinked = true
  @observable masters: MasterTemplatesObject | null = null
  @observable incompleteProspect = false
  @observable currentProspect: string | null = null
  @observable addedBy: string | null = null
  @observable notes: string | null = null
  @observable userEmail: string | null = null

  @action
  pushToToasts(toast: ToastType): void {
    const newToast = toast

    if (newToast.show === undefined) {
      newToast.show = true
    }

    if (newToast.delay === undefined) {
      newToast.delay = 3000
    }

    this.setToasts(this.toasts.concat(newToast))
  }

  @action
  popFromToasts(): void {
    const newToasts = _.cloneDeep(this.toasts)

    newToasts.shift()
    this.setToasts(newToasts)
  }

  @action
  setToasts(toasts: ToastType[]): void {
    this.toasts = toasts
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading
  }

  @action
  setUserInfoIsLoading(userInfoIsLoading: boolean): void {
    this.userInfoIsLoading = userInfoIsLoading
  }

  @action
  setUserInfo(userInfo: UserInfo | null): void {
    this.userInfo = userInfo

    if (userInfo) {
      this.setEmailLinked(userInfo.emailLinked)
    }
  }

  @action
  setUser(user: User | null): void {
    this.user = user
  }

  @action
  setProspect(prospect: Prospect | null): void {
    this.prospect = prospect

    this.setSendProspectChecked(prospect?.sendToMarketing === 'true')

    if (prospect?.sendToMarketing === 'true') {
      this.setSendProspectText(sendProspectTextOptions.checked)
    }

    switch (prospect?.copperStatus?.toLowerCase()) {
      case 'activated':
        this.setActivateManeuverOption(activateManeuverOptions.activated)
        this.setActivateManeuverChecked(activateManeuverOptions.activated.checked)
        break
      case 'email scheduled':
        this.setActivateManeuverOption(activateManeuverOptions.scheduled)
        this.setActivateManeuverChecked(activateManeuverOptions.scheduled.checked)
        break
      case 'mavericks ready':
        this.setActivateManeuverOption(activateManeuverOptions.ready)
        this.setActivateManeuverChecked(activateManeuverOptions.ready.checked)
        break
      case 'active':
        this.setActivateManeuverOption(activateManeuverOptions.active)
        this.setActivateManeuverChecked(activateManeuverOptions.active.checked)
        break
      case 'inactive':
      default:
        this.setActivateManeuverOption(activateManeuverOptions.inactive)
        this.setActivateManeuverChecked(activateManeuverOptions.inactive.checked)
    }

    this.setProspectDNCChecked(prospect?.dnc === 'true')
    if (prospect?.dnc === 'true') {
      this.setProspectDNCText(prospectDNCTextOptions.checked)
    }
  }

  @action
  setSendProspectChecked(sendProspectChecked: boolean): void {
    this.sendProspectChecked = sendProspectChecked
  }
  @action
  setProspectAddedBy(addedBy: string): void {
    this.addedBy = addedBy
  }

  @action
  setSendProspectText(sendProspectText: string): void {
    this.sendProspectCheckedText = sendProspectText
  }

  @action
  setActivateManeuverChecked(activateManeuverChecked: boolean): void {
    this.activateManeuverChecked = activateManeuverChecked
  }

  @action
  setActivateManeuverOption(activateManeuverOption: ActivateManeuverOption): void {
    this.activateManeuverOption = activateManeuverOption
  }

  @action
  setProspectDNCChecked(prospectDNCChecked: boolean): void {
    this.prospectDNCChecked = prospectDNCChecked
  }

  @action
  setProspectDNCText(prospectDNCText: string): void {
    this.prospectDNCText = prospectDNCText
  }

  @action
  setDeleteLeadConfirmationOpen(deleteLeadConfirmationOpen: boolean): void {
    this.deleteLeadConfirmationOpen = deleteLeadConfirmationOpen
  }

  @action
  setDeleteReason(deleteReason: string): void {
    this.deleteReason = deleteReason
  }

  @action
  setCacheValid(cacheValid: boolean): void {
    this.cacheValid = cacheValid
  }

  @action
  setEmailLinked(emailLinked: boolean): void {
    this.emailLinked = emailLinked
  }

  @action
  setMasters(masters: MasterTemplatesObject | null): void {
    this.masters = masters
  }

  @action
  setIncompleteProspect(incompleteProspect: boolean): void {
    this.incompleteProspect = incompleteProspect
  }

  @action
  setCurrentProspect(currentProspect: string | null): void {
    this.currentProspect = currentProspect
  }

  @action
  setCurrentUserEmail(userEmail: string | null): void {
    this.userEmail = userEmail
  }

  @action
  getUserInfo = flow(function*(this: Prospects) {
    this.setUserInfoIsLoading(true)

    try {
      const userInfo: UserInfo = yield API.get('mav-outlook-api', '/users/info', {})
      this.setUserInfo(userInfo)
    } catch (e) {
      // console.error(e)
    }

    this.setUserInfoIsLoading(false)
  })

  @action
  waitGetLead = flow(function*(this: Prospects, interval: NodeJS.Timeout, email?: string) {
    try {
      const response = yield API.get('mav-outlook-api', `/leads/${email}`, {})
      this.setProspect(response)
      this.setCacheValid(true)
      this.setIncompleteProspect(false)
      this.setIsLoading(false)

      clearInterval(interval)
    } catch (e) {
      // console.error(e)
    }
  })

  @action
  // eslint-disable-next-line consistent-return
  getLeadTransactionHistory = flow(function*(email?: string) {
    try {
      const response = yield API.get('mav-outlook-api', `/email-transactions/${email}`, {})
      const transactionsData: EmailTransaction[] = []

      const oldTerminology = {
        'First Touch': 'Rev Up',
        'Follow Up': 'Engaged',
        Nurture: 'Re-Engage',
      }

      response?.data?.map(item => {
        // setting up sent email Data
        let dataObj: EmailTransaction = {
          maneuver: oldTerminology[item?.campaignType],
          missionName: item?.missionName,
          transactionDate: item?.sentDate,
          emailBodySnippet: `${htmlToPlainText(item?.emailBody ?? '')?.slice(0, 55)}...`,
          isReply: false,
          touch: item?.emailName,
        }

        transactionsData.push(dataObj)

        if (item?.replied) {
          dataObj = {
            maneuver: item?.campaignType,
            missionName: item?.missionName,
            transactionDate: item?.replyDate,
            emailBodySnippet: `${htmlToPlainText(item?.replyBody ?? '')?.slice(0, 55)}...`,
            isReply: true,
            touch: item?.emailName,
          }
          transactionsData.push(dataObj)
        }
      })

      transactionsData.sort(
        (a, b) => new Date(a.transactionDate).valueOf() - new Date(b.transactionDate).valueOf(),
      )
      return transactionsData
    } catch (e) {
      // console.error(e)
    }
  })

  @action
  getLead = flow(function*(this: Prospects, email?: string, wait?: boolean) {
    this.setIsLoading(true)

    try {
      const response = yield API.get('mav-outlook-api', `/leads/${email}`, {})
      response.notifyOnComplete = response.notifyOnComplete
        ? JSON.parse(response.notifyOnComplete)
        : false

      this.setProspect(response)
      this.setCacheValid(true)
      this.setIncompleteProspect(false)
      this.setIsLoading(false)
    } catch (e) {
      // console.error(e)

      this.setProspect(null)
      this.setCacheValid(false)
      if (_.get(e, 'response.status') !== 404) {
        this.pushToToasts({
          type: ToastTypeEnum.ERROR,
          message: _.get(e, 'response.data.message', "Couldn't get lead"),
        })

        this.setIsLoading(false)
      } else if (wait) {
        // Passing the `wait` parameter to `this.getLead` will cause the function to continue to query
        // for the lead every second until it is found. This is helpful when the lead is being created
        // but isn't available yet, so the client will just query the API until a 2xx response happens.
        // Once the 2xx response happens, the prospect will be set in state and the interval will be cleared.

        // Issues with this should probably prompt a refactor using promises instead of intervals and timeouts.

        const interval = global.setInterval(() => {
          this.waitGetLead(interval, email)
        }, 1000)

        // If the lead isn't found within 10 seconds, clear the interval
        setTimeout(() => {
          clearInterval(interval)

          this.pushToToasts({
            type: ToastTypeEnum.ERROR,
            message: 'Unable to get newly created contact, try again in a few minutes',
          })

          this.setIsLoading(false)
        }, 10000)
      } else {
        this.setIsLoading(false)
      }
    }
  })

  @action
  handleSendProspectChange = flow(function*(this: Prospects, checked: boolean, email?: string) {
    const initialChecked = this.sendProspectChecked
    const initialText = this.sendProspectCheckedText

    if (checked) {
      this.setSendProspectChecked(true)
      this.setSendProspectText(sendProspectTextOptions.checked)
    } else {
      this.setSendProspectChecked(false)
      this.setSendProspectText(sendProspectTextOptions.unchecked)
    }

    const payload = {
      sendToMarketing: checked,
      leadId: this.prospect?.id,
    }

    const myInit = { body: payload }

    let contactSearch
    if (email) {
      contactSearch = email
    } else {
      contactSearch = this.prospect?.email
    }

    try {
      yield API.put('mav-outlook-api', `/leads/${contactSearch}`, myInit)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify marketing setting",
      })

      this.setSendProspectChecked(initialChecked)
      this.setSendProspectText(initialText)
    }
  })

  @action
  handleActivateManeuverChange = flow(function*(this: Prospects, checked: boolean, email?: string) {
    const initialChecked = this.activateManeuverChecked

    if (checked) {
      this.setActivateManeuverChecked(true)
    } else {
      this.setActivateManeuverChecked(false)
    }

    const payload = {
      deactivate: !checked,
      leadId: this.prospect?.id,
    }

    const myInit = { body: payload }

    let contactSearch
    if (email) {
      contactSearch = email
    } else {
      contactSearch = this.prospect?.email
    }

    try {
      yield API.put('mav-outlook-api', `/leads/${contactSearch}`, myInit)
      if (!checked && this.prospect) {
        this.setProspect({
          ...this.prospect,
          copperStatus: 'inactive',
          maneuver: '',
        })
      }
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify maneuver setting",
      })

      this.setActivateManeuverChecked(initialChecked)
    }
  })

  @action
  handleProspectDNCChange = flow(function*(this: Prospects, checked: boolean, email?: string) {
    const initialChecked = this.prospectDNCChecked
    const initialText = this.prospectDNCText

    if (checked) {
      this.setProspectDNCChecked(true)
      this.setProspectDNCText(prospectDNCTextOptions.checked)
    } else {
      this.setProspectDNCChecked(false)
      this.setProspectDNCText(prospectDNCTextOptions.unchecked)
    }

    const payload = {
      dnc: checked,
      leadId: this.prospect?.id,
    }

    const myInit = { body: payload }

    let contactSearch
    if (email) {
      contactSearch = email
    } else {
      contactSearch = this.prospect?.email
    }

    try {
      yield API.put('mav-outlook-api', `/leads/${contactSearch}`, myInit)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify DNC list setting",
      })

      this.setProspectDNCChecked(initialChecked)
      this.setProspectDNCText(initialText)
    }
  })

  @action
  handleDeleteLead = flow(function*(this: Prospects) {
    this.setIsLoading(true)

    try {
      yield API.del(
        'mav-outlook-api',
        `/leads/${this.prospect?.email}?reason=${this.deleteReason}`,
        {},
      )
      this.setDeleteReason('other')
      this.setDeleteLeadConfirmationOpen(false)
      this.setProspect(null)
      search.setCacheValid(false)
      related.setCacheValid(false)
    } catch (e) {
      // console.error(e)

      this.setDeleteLeadConfirmationOpen(false)
      this.setDeleteReason('other')
      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: 'Unable to delete lead',
      })
    }

    this.setIsLoading(false)
  })

  @action
  getMasters = flow(function*(this: Prospects) {
    this.setIsLoading(true)

    try {
      const response = yield API.get('mav-outlook-api', '/users/masterTemplates', {})
      // console.log(response)

      this.setMasters(response)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: 'Unable to get master templates',
      })
    }

    this.setIsLoading(false)
  })

  @action
  clearState(): void {
    this.setToasts([])
    this.setIsLoading(false)
    this.setUserInfoIsLoading(true)
    this.setUserInfo(null)
    this.setProspect(null)
    this.setSendProspectChecked(false)
    this.setSendProspectText(sendProspectTextOptions.unchecked)
    this.setActivateManeuverChecked(false)
    this.setActivateManeuverOption(activateManeuverOptions.inactive)
    this.setProspectDNCChecked(false)
    this.setProspectDNCText(prospectDNCTextOptions.unchecked)
    this.setDeleteLeadConfirmationOpen(false)
    this.setCacheValid(false)
    this.setEmailLinked(true)
    this.setMasters(null)
    this.setIncompleteProspect(false)
    this.setCurrentProspect(null)
    this.setCurrentUserEmail(null)
    this.setDeleteReason('other')
  }
}

export default new Prospects()
